import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomActionIconLink } from "../../../components/CustomActionIconLink/CustomActionIconLink";
import CustomPagination from "../../../components/CustomPagination/CustomPagination";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  advancedUserSearch,
  bulkUpdateStatus,
  getUserFilters,
  getUsers,
} from "../../../services/api/apiClient";
import {
  AdvancedSearchFilter,
  AdvancedSearchSelection,
  BooleanOperator,
  DateOperator,
  GetUserResult,
  GetUsersPaginatedResult,
  ListOperator,
  NumericOperator,
  Roles,
  StringOperator,
  UserQueryParams,
  UserStatusType,
} from "../../../services/api/types/users/users";
import { AttributesFilter } from "./components/AttributesFilter";
import { UsersTable } from "./components/UsersTable";
import { ValuesModal } from "./components/ValuesModal";
// Icons
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { MAX_LENGTH_SHORT_LABEL } from "../../../utils/inputLimits";
import { useAuth } from "../../../context/AuthContext";
import { brand } from "../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../utils/errorUtils";
import AddIcon from "@mui/icons-material/Add";
import refreshIcon from "../../../assets/refresh2.svg"

// Users with these statuses are excluded from the filtered selection (IGA-1040)
const PENDING_STATUSES = new Set<string>([
  UserStatusType.INVITATION_PENDING.toLowerCase(),
  UserStatusType.ARCHIVED_PENDING.toLowerCase(),
  UserStatusType.EMAIL_CHANGE_PENDING.toLowerCase(),
]);

const isPendingStatus = (status: string | undefined) =>
  PENDING_STATUSES.has((status ?? "").toLowerCase());

const countActiveFilters = (selections: AdvancedSearchSelection[]): number =>
  selections.reduce(
    (total, selection) =>
      total +
      selection.filters.filter(
        (f) => f.attribute.trim() && f.operator.trim() && f.value.trim(),
      ).length,
    0,
  );

interface UsersProps {}

export type ColId = "firstName" | "lastName" | "email" | "status";
export type Order = "asc" | "desc";
export type ActionId =
  | "view-report"
  | "send-email"
  | "edit-attributes"
  | "manage-assignments"
  | "archive-users"
  | "edit-training-records";
type ActionItem = {
  id: ActionId;
  to?: string;
  title: string;
  icon: React.ReactNode;
  target?: "_blank" | "_self";
};

const MANUAL_SELECTED_USERS_KEY = "manualSelectedUsers";
const MANUAL_SELECTED_USER_IDS_KEY = "manualSelectedUsersIds";
const FILTERED_SELECTED_USERS_KEY = "filteredSelectedUsers";
const FILTERED_SELECTED_USER_IDS_KEY = "filteredSelectedUsersIds";
const FILTERED_SELECTIONS_KEY = "filteredUserSelections";
const SELECTED_USERS_SOURCE_KEY = "selectedUsersSource";

type SelectionSource = "manual" | "filtered";

export const Users: React.FC<UsersProps> = () => {
  const [usersSearch, setUsersSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [manualSelectedUsers, setManualSelectedUsers] = useState<
    GetUserResult[]
  >([]);
  const [filteredSelectedUsers, setFilteredSelectedUsers] = useState<
    GetUserResult[]
  >([]);
  const [orderBy, setOrderBy] = useState<ColId>("firstName");
  const [sortOrder, setSortOrder] = useState<Order>("asc");
  const [usersResponse, setUsersResponse] = useState<GetUsersPaginatedResult>();
  const [filterLoading, setFilterLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selections, setSelections] = useState<AdvancedSearchSelection[]>([
    {
      filters: [
        {
          attribute: "",
          operator: "",
          value: "",
          groupHeader: "",
          filterGroup: "",
        },
      ],
    },
  ]);

  const [openValuesModal, setOpenValuesModal] = useState(false);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>();
  const [currentFilterIndex, setCurrentFilterIndex] = useState<number>(0);
  const [currentSelectionIndex, setCurrentSelectionIndex] = useState<number>(0);
  const [preselectedUsers, setPreselectedUsers] = useState<GetUserResult[]>([]);
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | ""
  >("");
  const [showArchivingModal, setShowArchivingModal] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  // Tracks an in-progress manual grid refresh (kept separate from tableLoading so
  // the grid stays visible while only the refresh button shows its loading state).
  const [refreshing, setRefreshing] = useState(false);
  const defaultSelections: AdvancedSearchSelection[] = [
    {
      filters: [
        {
          attribute: "",
          operator: "",
          value: "",
          groupHeader: "",
          filterGroup: "",
        },
      ],
    },
  ];

  const { auth } = useAuth();

  const selectedUsers = isEditMode
    ? filteredSelectedUsers
    : manualSelectedUsers;
  const anySelected = selectedUsers.length > 0;
  const activeFilterCount = isEditMode ? countActiveFilters(selections) : 0;

  const getStorageKeys = (type: SelectionSource) => {
    if (type === "manual") {
      return {
        usersKey: MANUAL_SELECTED_USERS_KEY,
        idsKey: MANUAL_SELECTED_USER_IDS_KEY,
      };
    }
    return {
      usersKey: FILTERED_SELECTED_USERS_KEY,
      idsKey: FILTERED_SELECTED_USER_IDS_KEY,
    };
  };

  const persistSelection = (
    type: SelectionSource,
    usersToPersist: GetUserResult[],
  ) => {
    localStorage.removeItem("selectedUsers");
    localStorage.removeItem("selectedUsersIds");

    const { usersKey, idsKey } = getStorageKeys(type);

    if (!usersToPersist.length) {
      localStorage.removeItem(usersKey);
      localStorage.removeItem(idsKey);
      if (type === "filtered") {
        localStorage.removeItem(FILTERED_SELECTIONS_KEY);
      }

      const otherType: SelectionSource =
        type === "manual" ? "filtered" : "manual";
      const { usersKey: otherUsersKey } = getStorageKeys(otherType);

      let hasOtherSelection = false;
      try {
        const otherStored = localStorage.getItem(otherUsersKey);
        if (otherStored) {
          const parsed = JSON.parse(otherStored);
          hasOtherSelection = Array.isArray(parsed) && parsed.length > 0;
        }
      } catch {
        hasOtherSelection = false;
      }

      if (hasOtherSelection) {
        localStorage.setItem(SELECTED_USERS_SOURCE_KEY, otherType);
      } else {
        localStorage.removeItem(SELECTED_USERS_SOURCE_KEY);
      }
      return;
    }

    localStorage.setItem(usersKey, JSON.stringify(usersToPersist));
    localStorage.setItem(
      idsKey,
      JSON.stringify(usersToPersist.map((user) => user.UserPortalUid)),
    );
    localStorage.setItem(SELECTED_USERS_SOURCE_KEY, type);
  };

  const persistFilteredSelections = (
    selectionsToPersist: AdvancedSearchSelection[],
  ) => {
    localStorage.setItem(
      FILTERED_SELECTIONS_KEY,
      JSON.stringify(selectionsToPersist),
    );
  };

  const clearAllSelectionStorage = () => {
    [
      MANUAL_SELECTED_USERS_KEY,
      MANUAL_SELECTED_USER_IDS_KEY,
      FILTERED_SELECTED_USERS_KEY,
      FILTERED_SELECTED_USER_IDS_KEY,
      FILTERED_SELECTIONS_KEY,
      SELECTED_USERS_SOURCE_KEY,
      "selectedUsers",
      "selectedUsersIds",
    ].forEach((key) => localStorage.removeItem(key));
  };

  const debouncedSearch = useDebounce(usersSearch, 500);
  const { portals } = usePortal();
  const { showToast } = useToast();
  const { portalUid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldShowLastPage, setShouldShowLastPage] = useState<boolean>(() =>
    Boolean(
      location.state &&
      (location.state as { showLastPage?: boolean }).showLastPage,
    ),
  );

  const fetchSeqRef = useRef(0);
  const lastPortalUidForFetchRef = useRef<string | undefined>(portalUid);
  const shouldRefreshFilterRef = useRef(true);

  useEffect(() => {
    if (
      location.state &&
      ((location.state as { showLastPage?: boolean }).showLastPage ||
        (location.state as { refreshFilter?: boolean }).refreshFilter)
    ) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const base = `/portal-configuration/${portalUid}`;
  const actions: ActionItem[] = [
    {
      id: "view-report",
      to: `${base}/users/reports/user-report`,
      title: "View report",
      icon: <DescriptionOutlinedIcon />,
      target: "_blank",
    },
    {
      id: "send-email",
      to: `${base}/users/email-send`,
      title: "Send email",
      icon: <EmailOutlinedIcon />,
    },
    {
      id: "edit-attributes",
      to: `${base}/users/edit-attributes`,
      title: "Edit attributes",
      icon: <DriveFileRenameOutlineOutlinedIcon />,
    },
    {
      id: "manage-assignments",
      to: `${base}/users/manage-assignments`,
      title: "Manage assignments",
      icon: <AssignmentOutlinedIcon />,
    },
    {
      id: "archive-users",
      title: "Archive users",
      icon: <ArchiveOutlinedIcon />,
    },
    {
      id: "edit-training-records",
      to: `${base}/users/edit-training-records`,
      title: "Training records",
      icon: <LibraryBooksOutlinedIcon />,
    },
  ];

  const fetchUsers = async (
    extraParams: Partial<UserQueryParams> = {},
    options?: { forceLastPage?: boolean; surfaceErrors?: boolean },
  ) => {
    if (!portalUid) return;

    const seq = (fetchSeqRef.current += 1);
    const effectiveLimit = extraParams.limit ?? limit;
    const URLParams: UserQueryParams = {
      limit: effectiveLimit,
      search: extraParams.search,
      ...extraParams,
    };

    const params = new URLSearchParams(window.location.search);
    const search = params.get("search");

    if (search) {
      URLParams.search = search;
    }

    try {
      let response: GetUsersPaginatedResult = await getUsers(
        portalUid,
        URLParams,
      );

      if (seq !== fetchSeqRef.current) return;

      if (options?.forceLastPage) {
        const lastPage = Math.max(
          1,
          Math.ceil(response.count / effectiveLimit),
        );
        const desiredOffset = (lastPage - 1) * effectiveLimit;
        const currentOffset = URLParams.offset ?? 0;

        if (desiredOffset !== currentOffset) {
          response = await getUsers(portalUid, {
            ...URLParams,
            offset: desiredOffset,
          });
        }

        if (seq !== fetchSeqRef.current) return;

        setPage(lastPage);
        setShouldShowLastPage(false);
      }

      setUsersResponse(response);
    } catch (error) {
      console.log("ERROR: ", error);
      if (seq === fetchSeqRef.current) {
        if (options?.surfaceErrors) {
          showToast(
            extractErrorMessage(error, "Failed to refresh users."),
            "error",
          );
        } else {
          setUsersResponse(undefined);
        }
      }
      if (options?.forceLastPage) {
        setShouldShowLastPage(false);
      }
    }
  };

  const previousPortalUid = useRef<string | undefined>(portalUid);

  // Clear everything when changing portal
  useEffect(() => {
    if (
      previousPortalUid.current !== undefined &&
      previousPortalUid.current !== portalUid
    ) {
      clearAllSelectionsAndFilters();
    }

    // Update the ref after the comparison
    previousPortalUid.current = portalUid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  useEffect(() => {
    const parseStored = <T,>(key: string): T | null => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        return parsed as T;
      } catch {
        return null;
      }
    };

    const parseStoredUsers = (key: string): GetUserResult[] => {
      const parsed = parseStored<unknown>(key);
      return Array.isArray(parsed) ? (parsed as GetUserResult[]) : [];
    };

    const parseStoredSelections = (key: string): AdvancedSearchSelection[] => {
      const parsed = parseStored<unknown>(key);
      return Array.isArray(parsed)
        ? (parsed as AdvancedSearchSelection[])
        : defaultSelections;
    };

    const manualFromStorage = parseStoredUsers(MANUAL_SELECTED_USERS_KEY);
    const filteredFromStorage = parseStoredUsers(FILTERED_SELECTED_USERS_KEY);
    const storedFilteredSelections = parseStoredSelections(
      FILTERED_SELECTIONS_KEY,
    );
    const legacyFiltered = filteredFromStorage.length
      ? []
      : parseStoredUsers("selectedUsers");

    if (legacyFiltered.length && !filteredFromStorage.length) {
      persistSelection("filtered", legacyFiltered);
      localStorage.removeItem("selectedUsers");
      localStorage.removeItem("selectedUsersIds");
    }

    const filteredToUse = filteredFromStorage.length
      ? filteredFromStorage
      : legacyFiltered;

    const filteredSelectedToUse = filteredToUse.filter(
      (u) => !isPendingStatus(u.status),
    );
    const manualToUse = manualFromStorage.filter(
      (u) => !isPendingStatus(u.status),
    );

    setSelections(storedFilteredSelections);
    setFilteredSelectedUsers(filteredSelectedToUse);
    setPreselectedUsers(filteredToUse);

    let source = localStorage.getItem(
      SELECTED_USERS_SOURCE_KEY,
    ) as SelectionSource | null;

    const hasManual = manualToUse.length > 0;
    const hasFiltered = filteredSelectedToUse.length > 0;

    if (source === "manual" && !hasManual) {
      source = hasFiltered ? "filtered" : null;
    } else if (source === "filtered" && !hasFiltered) {
      source = hasManual ? "manual" : null;
    }

    if (!source) {
      if (hasFiltered) source = "filtered";
      else if (hasManual) source = "manual";
    }

    if (source) {
      localStorage.setItem(SELECTED_USERS_SOURCE_KEY, source);
    } else {
      localStorage.removeItem(SELECTED_USERS_SOURCE_KEY);
    }

    // Load manual selections if source is manual
    if (source === "manual" && hasManual) {
      setManualSelectedUsers(manualToUse);
    } else {
      setManualSelectedUsers([]);
    }

    setIsEditMode(source === "filtered");
    setStorageLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!storageLoaded || !isEditMode || !shouldRefreshFilterRef.current)
      return;
    shouldRefreshFilterRef.current = false;
    setTableLoading(true);
    handleSearch(0).finally(() => setTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageLoaded, isEditMode]);

  useEffect(() => {
    if (!storageLoaded) return;
    if (isEditMode) return;

    setTableLoading(true);
    const portalChanged = lastPortalUidForFetchRef.current !== portalUid;
    if (portalChanged) {
      lastPortalUidForFetchRef.current = portalUid;

      // Reset to page 1 first (avoid fetching the new portal with old offset).
      if (page !== 1) {
        setPage(1);
        setShouldShowLastPage(false);
        setTableLoading(false);
        return;
      }
    }

    const offset = portalChanged || shouldShowLastPage ? 0 : (page - 1) * limit;
    fetchUsers(
      {
        limit,
        search: debouncedSearch,
        offset,
      },
      { forceLastPage: shouldShowLastPage },
    ).finally(() => setTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storageLoaded,
    isEditMode,
    limit,
    debouncedSearch,
    shouldShowLastPage,
    page,
    portalUid,
  ]);

  const handleRowsChange = (e: SelectChangeEvent<number>) => {
    const nextLimit = Number(e.target.value);
    setLimit(nextLimit);
    setPage(1);
  };

  const handlePageNumberClick = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleNextClick = () => {
    if (!isEditMode && !usersResponse?.nextPage) return;
    setPage((prev) => prev + 1);
  };

  const handlePrevClick = () => {
    if (!isEditMode && !usersResponse?.previousPage) return;
    setPage((prev) => prev - 1);
  };

  const toggleSort = (col: ColId) => {
    const nextSort: Order =
      col === orderBy ? (sortOrder === "asc" ? "desc" : "asc") : "desc";
    setOrderBy(col);
    setSortOrder(nextSort);

    // When in editMode don't call backend (sorting handled on client-side)
    if (!isEditMode) {
      fetchUsers({
        orderBy: col,
        sort: nextSort,
        limit,
      });
    } else {
      setPage(1);
    }
  };

  const saveSelectedUsersIds = () => {
    const selectionType: SelectionSource = isEditMode ? "filtered" : "manual";
    persistSelection(selectionType, selectedUsers);
  };

  const handleActionClick = (actionId: ActionId) => {
    saveSelectedUsersIds();

    if (actionId === "view-report") {
      localStorage.setItem("viewReportMode", "true");
    } else if (actionId === "archive-users") {
      setShowArchivingModal(true);
      return;
    }
  };

  const clearAllSelectionsAndFilters = () => {
    clearAllSelectionStorage();

    //  Clear router state so a refresh/revisit doesn't bring it back
    navigate(`${base}/users/`, {
      replace: true,
      state: null,
    });

    // Reset component state
    setSelections([
      {
        filters: [
          {
            attribute: "",
            operator: "",
            value: "",
            groupHeader: "",
            filterGroup: "",
          },
        ],
      },
    ]);
    setUsersSearch("");
    setShowFilter(false);
    setManualSelectedUsers([]);
    setFilteredSelectedUsers([]);
    setPreselectedUsers([]);
    setIsEditMode(false);
    setPage(1);
  };

  const handleSelectionChange = (updatedUsers: GetUserResult[]) => {
    if (isEditMode) {
      setFilteredSelectedUsers(updatedUsers);
      persistSelection("filtered", updatedUsers);
    } else {
      setManualSelectedUsers(updatedUsers);
      persistSelection("manual", updatedUsers);
    }
  };

  const handleChange = <K extends keyof AdvancedSearchFilter>(
    selectionIndex: number,
    filterIndex: number,
    field: K,
    value: AdvancedSearchFilter[K],
    groupHeader?: string,
    filterGroup?: string,
    initialValue?: string,
  ) => {
    const newSelections = [...selections];
    const newFilters = [...newSelections[selectionIndex].filters];

    if (field === "attribute") {
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        attribute: value as string,
        operator: "",
        value: initialValue ?? "",
        groupHeader: groupHeader ?? newFilters[filterIndex].groupHeader ?? "",
        filterGroup: filterGroup ?? newFilters[filterIndex].filterGroup ?? "",
      };
    } else if (field === "operator") {
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        operator: value as AdvancedSearchFilter["operator"],
      };
    } else {
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        [field]: value,
      };
    }

    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: newFilters,
    };
    setSelections(newSelections);
    persistFilteredSelections(newSelections);
  };
  const handleSearch = async (
    selectionIndex: number,
    options?: { isRefresh?: boolean },
  ) => {
    let hasError = false;

    selections.forEach((selection) => {
      selection.filters.forEach((filter) => {
        if (!filter.attribute.trim()) {
          hasError = true;
        }
        if (!filter.operator.trim()) {
          hasError = true;
        }
        if (!filter.value.trim()) {
          hasError = true;
        }
      });
    });

    if (hasError) {
      return;
    }

    try {
      if (portals.selectedPortal) {
        setFilterLoading(true);
        const transformedSelections = selections.map((selection) => ({
          ...selection,
          filters: selection.filters.map(({ groupHeader, ...rest }) => ({
            ...rest,
            // attribute:
            //   groupHeader === "User attributes"
            //     ? `user.${rest.attribute}`
            //     : `userAssignment.${rest.attribute}`,
            value: rest.value
              ? rest.value
                  .split(",")
                  .map((v) => v.trim())
                  .join("|")
              : rest.value,
          })),
        }));

        const response: { finResult: GetUserResult[] } =
          await advancedUserSearch(portals.selectedPortal.portalUid, {
            selections: transformedSelections,
          });

        if (response.finResult) {
          // Exclude pending users so they can't be selected in edit mode (IGA-1040)
          const selectableUsers = response.finResult.filter(
            (u) => !isPendingStatus(u.status),
          );

          if (options?.isRefresh) {
            const selectedIds = new Set(
              filteredSelectedUsers.map((u) => u.UserPortalUid),
            );
            const preservedSelection = selectableUsers.filter((u) =>
              selectedIds.has(u.UserPortalUid),
            );
            setFilteredSelectedUsers(preservedSelection);
            persistSelection("filtered", preservedSelection);
          } else {
            setFilteredSelectedUsers(selectableUsers);
            setPage(1);
            persistSelection("filtered", response.finResult);
          }

          setPreselectedUsers(response.finResult);
          setIsEditMode(true);
          persistFilteredSelections(selections);
        }

        const newSelections = [...selections];
        newSelections[selectionIndex] = {
          ...newSelections[selectionIndex],
        };
        setSelections(newSelections);
        setShowFilter(false);
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response.data, "error");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    const done = () => setRefreshing(false);

    if (isEditMode) {
      handleSearch(0, { isRefresh: true }).finally(done);
    } else {
      fetchUsers(
        {
          limit,
          search: debouncedSearch,
          offset: (page - 1) * limit,
          orderBy,
          sort: sortOrder,
        },
        { surfaceErrors: true },
      ).finally(done);
    }
  };

  const handleArchiveUsers = async () => {
    if (!portalUid) return;

    // only skip already archived
    const usersToArchive = selectedUsers.filter(
      (u) => u.UserPortalUid && (u.status ?? "").toLowerCase() !== "archived",
    );

    // if everything selected is already archived, just close modal and exit
    if (!usersToArchive.length) {
      setShowArchivingModal(false);
      return;
    }

    try {
      await bulkUpdateStatus(portalUid, {
        users: usersToArchive.map((u) => u.UserPortalUid),
        adminUser: auth.user?.UserPortalUid || "",
      });

      // clear selection + refresh
      clearAllSelectionStorage();
      setManualSelectedUsers([]);
      setFilteredSelectedUsers([]);
      setPreselectedUsers([]);
      setIsEditMode(false);
      setPage(1);

      await fetchUsers({ limit, offset: 0, search: debouncedSearch });

      setShowArchivingModal(false);
    } catch (error: any) {
      showToast(error?.response?.data ?? "Failed to archive users.", "error");
    }
  };

  const addRow = (selectionIndex: number) => {
    const newSelections = [...selections];
    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: [
        ...newSelections[selectionIndex].filters,
        {
          attribute: "",
          operator: "",
          value: "",
          groupHeader: "",
          filterGroup: "",
        },
      ],
    };
    setSelections(newSelections);
    persistFilteredSelections(newSelections);
  };

  const removeRow = (selectionIndex: number, filterIndex: number) => {
    if (selections[selectionIndex].filters.length <= 1) return;
    const newSelections = [...selections];
    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: newSelections[selectionIndex].filters.filter(
        (_, i) => i !== filterIndex,
      ),
    };
    setSelections(newSelections);
    persistFilteredSelections(newSelections);
  };

  const addSelection = () => {
    const newSelection: AdvancedSearchSelection = {
      filters: [
        {
          attribute: "",
          operator: "",
          value: "",
          groupHeader: "",
          filterGroup: "",
        },
      ],
    };
    setSelections((prev) => {
      const nextSelections = [...prev, newSelection];
      persistFilteredSelections(nextSelections);
      return nextSelections;
    });
  };

  const removeSelection = (selectionIndex: number) => {
    if (selections.length === 1) return;
    setSelections((prev) => {
      const nextSelections = prev.filter((_, i) => i !== selectionIndex);
      persistFilteredSelections(nextSelections);
      return nextSelections;
    });
  };

  const getValuesForAttribute = async (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    selectionIndex: number,
    filterIndex: number,
  ) => {
    const filters = selections[selectionIndex].filters;
    if (!filters[filterIndex]?.attribute.trim()) {
      return;
    }
    try {
      if (portals.selectedPortal) {
        setCurrentFilterIndex(filterIndex);
        setCurrentSelectionIndex(selectionIndex);
        setSelectedOperator(operator);
        setOpenValuesModal(true);
        setValuesLoading(true);
        const response: Partial<GetUserResult>[] = await getUserFilters(
          portals.selectedPortal.portalUid,
          {
            userAttributeKey: attribute,
            userAttributeValue: value,
            operator: operator,
          },
        );
        const valuesArray = response
          .map((item) => item[attribute as keyof GetUserResult])
          .filter((value): value is string => typeof value === "string");
        setSelectedValues(valuesArray);
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response.data, "error");
    } finally {
      setValuesLoading(false);
    }
  };

  const addSelectedValuesToFilter = (selectedValues: string[]) => {
    const newSelections = [...selections];
    const currentSelection = newSelections[currentSelectionIndex];
    const currentFilters = [...currentSelection.filters];
    const currentFilter = currentFilters[currentFilterIndex];
    currentFilters[currentFilterIndex] = {
      ...currentFilter,
      value: selectedValues.join("|"),
    };
    newSelections[currentSelectionIndex] = {
      ...currentSelection,
      filters: currentFilters,
    };
    setSelections(newSelections);
    persistFilteredSelections(newSelections);
    setOpenValuesModal(false);
  };

  // Client-side logic when in edit mode for pagination, sorting, etc..
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;

  const sortedPreselectedUsers: GetUserResult[] = [...preselectedUsers].sort(
    (a, b) => {
      const aValue = a[orderBy]?.toLowerCase() || "";
      const bValue = b[orderBy]?.toLowerCase() || "";

      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    },
  );

  const searchValue = debouncedSearch?.trim().toLowerCase() ?? "";

  const filteredPreselectedUsers: GetUserResult[] =
    !isEditMode || searchValue === ""
      ? sortedPreselectedUsers
      : sortedPreselectedUsers.filter(
          (user: GetUserResult) =>
            (user.firstName ?? "").toLowerCase().includes(searchValue) ||
            (user.lastName ?? "").toLowerCase().includes(searchValue) ||
            (user.email ?? "").toLowerCase().includes(searchValue),
        );

  const paginatedPreselectedUsers = filteredPreselectedUsers.slice(
    startIndex,
    endIndex,
  );

  const serverRespRows = usersResponse?.rows ?? [];
  const serverRespCount = usersResponse?.count ?? 0;
  const clientCount = preselectedUsers.length;
  const itemsCount = isEditMode ? clientCount : serverRespCount;
  const rows = isEditMode
    ? preselectedUsers.length > 4
      ? limit
      : preselectedUsers.length
    : serverRespCount > 4
      ? limit
      : serverRespCount;
  const totalPages = isEditMode
    ? Math.ceil(preselectedUsers.length / limit)
    : Math.ceil(serverRespCount / limit);
  const users = isEditMode ? paginatedPreselectedUsers : serverRespRows;

  const isAdmin =
    auth.user?.roles?.includes(Roles.ADMIN) ||
    auth.user?.roles?.includes(Roles.PORTAL_ADMIN) ||
    auth.user?.roles?.includes(Roles.MAIN_ADMIN) ||
    auth.user?.roles?.includes(Roles.SYSTEM_ADMIN);

  useEffect(() => {
    if (itemsCount <= 0) return;

    // if limit isn't a multiple of 10 and isn't "All", reset to 10
    const isValid = limit % 10 === 0 || limit === itemsCount;
    if (!isValid) {
      setLimit(10);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsCount]);

  usePageMetadata(
    {
      title: "User search",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
        },
      ],
    },
    [portalUid],
  );

  return (
    <Box>
      <Paper sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder="Search by first, last name or email"
              fullWidth
              value={usersSearch}
              onChange={(e) => {
                setUsersSearch(e.target.value);
                if (isEditMode) setPage(1);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-root": {
                  bgcolor: "#EAECF0",
                },
              }}
            />
            <Tooltip title="Refreshes grid data only, not the full page.">
              <span>
                <IconButton
                  aria-label="Refresh"
                  disabled={refreshing}
                  onClick={handleRefresh}
                >
                  {refreshing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <img src={refreshIcon} alt="refresh" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Stack direction="row" alignItems="center" mt={2}>
            {anySelected && (
              <Box display="flex" alignItems="center" gap={1} mr={1}>
                <Typography variant="h6">Selected users</Typography>
                <Box
                  minWidth={24}
                  height={24}
                  px={0.75}
                  borderRadius="50%"
                  bgcolor="#EFF8FF"
                  border="1px solid #B2DDFF"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography color="#175CD3">
                    {selectedUsers.length}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box ml="auto" display="flex" alignItems="center" gap={1.5}>
              {anySelected && (
                <Stack direction="row" spacing={1}>
                  {actions.map((a) => (
                    <CustomActionIconLink
                      key={a.id}
                      to={a.to}
                      state={{ selectedUsers }}
                      title={a.title}
                      icon={a.icon}
                      target={a.target}
                      disabled={
                        a.id === "edit-training-records" &&
                        selectedUsers.length > 1
                      }
                      hidden={a.id === "edit-training-records" && !isAdmin}
                      onClick={() => handleActionClick(a.id)}
                    />
                  ))}
                  <Divider orientation="vertical" flexItem />
                </Stack>
              )}
              <Button
                component={Link}
                to={`${base}/users/add-new-user`}
                startIcon={<AddIcon />}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                  },
                }}
              >
                Add new user
              </Button>
              <Button
                id="users-attributes-filter-button"
                onClick={() => {
                  setShowFilter(true);
                }}
                startIcon={<FilterListIcon sx={{ color: "white" }} />}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  ...(activeFilterCount > 0 && {
                    border: "2px solid #175CD3",
                  }),
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                  },
                }}
              >
                <Typography>
                  {activeFilterCount > 0
                    ? `Filter (${activeFilterCount})`
                    : "Filter"}
                </Typography>
              </Button>
            </Box>
          </Stack>
          {(selectedUsers.length !== 0 || activeFilterCount > 0) && (
            <Box sx={{ marginTop: "2rem" }}>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    activeFilterCount > 0 ? "space-between" : "flex-end",
                  marginTop: "0.5rem",
                }}
              >
                {activeFilterCount > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {activeFilterCount} filter
                    {activeFilterCount === 1 ? "" : "s"} applied
                  </Typography>
                )}
                <Button
                  sx={{
                    backgroundColor: "transparent !important",
                    color: brand[600],
                  }}
                  onClick={clearAllSelectionsAndFilters}
                >
                  Clear all
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        <>
          <AttributesFilter
            loading={filterLoading}
            addRow={addRow}
            selections={selections}
            handleChange={handleChange}
            handleSearch={handleSearch}
            removeRow={removeRow}
            getValuesForAttribute={getValuesForAttribute}
            setShowFilter={setShowFilter}
            showFilter={showFilter}
            addSelection={addSelection}
            removeSelection={removeSelection}
            clearAll={clearAllSelectionsAndFilters}
          />
          <ValuesModal
            buttonLoading={valuesLoading}
            openModal={openValuesModal}
            setOpenModal={setOpenValuesModal}
            values={selectedValues}
            valuesLoading={valuesLoading}
            onAddSelectedValues={addSelectedValuesToFilter}
            operator={selectedOperator}
            preSelectedValues={
              selections[currentSelectionIndex]?.filters[currentFilterIndex]
                ?.value ?? ""
            }
          />
          {tableLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <UsersTable
                users={users}
                sortOrder={sortOrder}
                orderBy={orderBy}
                linkTo={`${base}/users/add-new-user`}
                onToggleSort={toggleSort}
                selectedUsers={selectedUsers}
                onSelectionChange={handleSelectionChange}
                isFiltered={isEditMode}
              />
              <CustomPagination
                rows={rows}
                itemsCount={itemsCount}
                handleRowsChange={handleRowsChange}
                totalPages={totalPages}
                currentPage={page}
                onPageNumberClick={handlePageNumberClick}
                onNextClick={handleNextClick}
                onPreviousClick={handlePrevClick}
              />
            </>
          )}
          <ConfirmModal
            openConfirmModal={showArchivingModal}
            setOpenConfirmModal={setShowArchivingModal}
            modalTitle="Archive users?"
            modalSubtitle="Are you sure you want to archive the selected users? They will no longer have access but can be restored later."
            confirmButtonClick={handleArchiveUsers}
            confirmButtonText="Yes, I am sure"
            cancelButtonText="No, I don't want"
          />
        </>
      </Paper>
    </Box>
  );
};
